/**
 * Facility — the unified Source of Truth that backs both
 * UW Information → Schedule of Location AND Schedule → Facility
 * Schedule. Edits in either section route through `useFacilities`,
 * which persists to the real `/v2/submissions/{id}/locations` backend
 * (the `endpoints/facilities.ts` adapter maps this shape to/from the
 * backend location row).
 *
 * Field shape rationale:
 *   - Every editable column from the two FE tables is here ONCE,
 *     dedup'd (location/facility name → `name`; PL/GL retro from both
 *     tables → one canonical pair).
 *   - Derived/calculated values (occupancy, equivalency, premiums)
 *     are NOT persisted — they're recomputed live in the FE from the
 *     fields below.
 *   - Numerics are kept as strings so the FE's editable cells (which
 *     control text inputs) round-trip the raw value without parse →
 *     format ambiguity. Adapters convert to numbers at the calc layer.
 *
 * Wire convention: the FE keeps camelCase end-to-end; the
 * `endpoints/facilities.ts` adapter translates to/from the backend's
 * snake_case `SubmissionLocationWire`.
 */

export interface Facility {
    /** Server-assigned UUID. Empty string from the FE while creating
     *  optimistically — the endpoint replaces it on POST response. */
    id: string;
    /** Foreign key to the submission. Used as the localStorage scope
     *  key in the mock store (`orion.facilities.<submissionId>`). */
    submissionId: string;
    /** The global `/v2/facilities` catalog id this submission location
     *  links to (`location.facility_id`). Set when a catalog facility is
     *  picked (Schedule row or Regulatory Add Row); "" when unlinked. Other
     *  sections (Surveys, Operational staffing, Occupancies) key on this
     *  catalog id, so it's the join handle. */
    catalogFacilityId: string;

    // ── Identity ─────────────────────────────────────────────
    /** Shown as "Location Name" in Schedule of Location and
     *  "Facility Name" in Facility Schedule — the same field. */
    name: string;
    /** State / federal provider number. Free text. */
    providerNumber: string;
    /** When the operator acquired the facility (MM/DD/YYYY). */
    acquired: string;
    operator: string;

    // ── Address ──────────────────────────────────────────────
    address: string;
    city: string;
    /** 2-letter US state code. */
    state: string;
    zip: string;
    county: string;
    /** Urban vs rest-of-state — drives the rates table lookup
     *  ("CA" vs "CA - ROS"). Empty defaults to urban for now. */
    geo: "" | "Rural" | "Urban";
    /** The location's stored actuarial rate, as the pricing-model import
     *  landed it (Schedule sheet column Y). Read from the backend and used
     *  as the Rate column's value in preference to the rates-table lookup,
     *  so an imported submission shows the rate the underwriter priced with.
     *
     *  Cleared to "" when the user edits State or Geo — those two compose
     *  the lookup key, so either edit means the stored rate no longer
     *  describes the row and the table takes over again.
     *
     *  Read-only from the wire: it is deliberately absent from
     *  `emptyFacility()`, so it is not in `WRITABLE_KEYS` and no PATCH body
     *  carries it. The Schedule's own save still persists the rate through
     *  `LocationComputedFields`, which sends whatever the Rate column is
     *  currently showing. */
    actuarialRate: string;

    // ── Coverage / risk ──────────────────────────────────────
    /** PL retro-active date (MM/DD/YYYY). */
    plRetroactiveDate: string;
    /** GL retro-active date (MM/DD/YYYY). */
    glRetroactiveDate: string;
    /** Retention amount in dollars; chosen via MoneySelect on the row. */
    retention: string;
    /** The location's stored SIR credit, as the pricing-model import landed
     *  it (Schedule sheet column AC, a fraction). Used as the SIR Credit
     *  column's value in preference to the credit-factors lookup, which is
     *  exact-match on the retention and resolves to nothing for a retention
     *  between the carrier's tiers.
     *
     *  Cleared to "" when the user edits Retention, the one input the
     *  lookup takes. Read-only from the wire on the same terms as
     *  `actuarialRate` above. */
    sirCredit: string;
    plLimit: string;
    glLimit: string;

    // ── Census (user-typed bed counts) ───────────────────────
    censusSkilled: string;
    /** Reserved for the future Memory Care column. Defaults to "0". */
    censusMemoryCare: string;
    censusAssisted: string;
    censusIndependent: string;
    censusAddlExposure: string;

    // ── User-input adjustment ────────────────────────────────
    /** Maturity Credit as a percent integer (e.g. "80" = 80%).
     *  Default empty → calc treats as 100% retained. */
    maturityCredit: string;

    // ── Licensed bed counts ──────────────────────────────────
    licensedSnf: string;
    /** Reserved — see censusMemoryCare. */
    licensedMc: string;
    licensedAlf: string;
    licensedIlf: string;

    // ── Archive state ────────────────────────────────────────
    /** Archived (soft-deleted) — OR-1431. The facility stays on the
     *  submission with its data intact, but is excluded from everything
     *  pricing-related and renders struck-through + read-only at the bottom
     *  of every table. Persisted as the backend's `is_excluded`.
     *
     *  User-facing copy says "archive"/"restore"; the wire says `is_excluded`.
     *  Client-written (it IS in `WRITABLE_KEYS`), so it must NOT be added to
     *  `serverOwned()` in `useFacilities`. */
    isExcluded: boolean;

    // ── Server metadata ──────────────────────────────────────
    /** Original submission's id when the backend flags this facility as
     *  a duplicate of a previously-submitted location; "" otherwise.
     *  Read-only — drives the per-row "previously submitted" flag. */
    duplicateSubmissionId: string;
    createdAt: string;
    updatedAt: string;
}

/** Subset of `Facility` the client can write. Excludes server-managed
 *  fields (id, submissionId, duplicateSubmissionId, createdAt,
 *  updatedAt). All keys optional so a PATCH can touch a single column. */
export type FacilityWritePayload = Partial<
    Omit<Facility, "id" | "submissionId" | "duplicateSubmissionId" | "createdAt" | "updatedAt">
>;

/** Factory for a brand-new facility with every editable field blank.
 *  Used by "Add Row" actions in both UIs. */
export function emptyFacility(): FacilityWritePayload {
    return {
        catalogFacilityId: "",
        name: "",
        providerNumber: "",
        acquired: "",
        operator: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        geo: "",
        plRetroactiveDate: "",
        glRetroactiveDate: "",
        retention: "",
        plLimit: "",
        glLimit: "",
        censusSkilled: "",
        censusMemoryCare: "",
        censusAssisted: "",
        censusIndependent: "",
        censusAddlExposure: "",
        maturityCredit: "",
        licensedSnf: "",
        licensedMc: "",
        licensedAlf: "",
        licensedIlf: "",
        // A new row is never archived. Listed here (and not only on the
        // interface) because `WRITABLE_KEYS` is derived from this factory —
        // leaving it out would make `diffPayload` blind to archive/restore,
        // so the toggle would never reach the backend.
        isExcluded: false,
    };
}
